import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { Loader2, Sparkles, ListChecks } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog generico per pulire i riferimenti orfani (tipologia/categoria
 * cancellata) di fornitori o ingredienti.
 *
 * Props:
 *  - open / onClose
 *  - mode: "fornitori" | "ingredienti"
 *  - getAuthHeaders: funzione che restituisce gli headers di auth
 *  - localeId: opzionale, usato in mode=ingredienti per filtrare
 *  - onSaved: callback dopo applicazione del fix
 */
export default function SistemaOrfaniDialog({ open, onClose, mode, getAuthHeaders, localeId, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orfani, setOrfani] = useState([]);
  // Mappa item.id → categoria_id scelta (o "" per Senza categoria)
  const [scelte, setScelte] = useState({});
  // Lista delle categorie disponibili (per fornitori: tipologie globali; per ingredienti: per locale)
  const [categorieDisponibili, setCategorieDisponibili] = useState([]);

  const isFornitori = mode === "fornitori";
  const labelEntita = isFornitori ? "fornitore" : "ingrediente";
  const labelEntitaPlur = isFornitori ? "fornitori" : "ingredienti";
  const labelCategoria = isFornitori ? "tipologia" : "categoria";

  const endpointGet = isFornitori ? "/fornitori/orfani-fix" : "/ingredienti/orfani-fix";
  const endpointPost = isFornitori ? "/fornitori/orfani-fix" : "/ingredienti/orfani-fix";

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = !isFornitori && localeId ? { locale_id: localeId } : {};
        const res = await axios.get(`${API}${endpointGet}`, {
          headers: getAuthHeaders(),
          params,
        });
        if (cancel) return;
        const list = res.data.orfani || [];
        setOrfani(list);
        if (isFornitori) {
          setCategorieDisponibili(res.data.tipologie_disponibili || []);
        } else {
          // Per ingredienti: categorie_per_locale → flat (un solo locale alla volta)
          const cpl = res.data.categorie_per_locale || {};
          const flat = localeId && cpl[localeId] ? cpl[localeId] : Object.values(cpl).flat();
          setCategorieDisponibili(flat);
        }
        // Pre-popola le scelte con i suggerimenti (o "" = Senza categoria)
        const initial = {};
        list.forEach(o => {
          initial[o.id] = o.suggested_id || "";
        });
        setScelte(initial);
      } catch (e) {
        toast.error(`Errore nel caricamento orfani`);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, mode, localeId]);

  const conSuggerimento = useMemo(
    () => orfani.filter(o => o.suggested_id).length,
    [orfani]
  );

  const handleAcceptAll = () => {
    const next = { ...scelte };
    orfani.forEach(o => {
      if (o.suggested_id) next[o.id] = o.suggested_id;
    });
    setScelte(next);
    toast.success(`Accettati ${conSuggerimento} suggerimenti`);
  };

  const handleClearAll = () => {
    const next = {};
    orfani.forEach(o => { next[o.id] = ""; });
    setScelte(next);
    toast.info(`Tutti messi a "Senza ${labelCategoria}"`);
  };

  const handleApply = async () => {
    setSaving(true);
    try {
      const items = orfani.map(o => {
        const cid = scelte[o.id];
        if (isFornitori) {
          return { fornitore_id: o.id, tipologia_id: cid || null };
        }
        return { ingrediente_id: o.id, categoria_id: cid || null };
      });
      const res = await axios.post(`${API}${endpointPost}`, { items }, { headers: getAuthHeaders() });
      toast.success(`${res.data.updated || 0} ${labelEntitaPlur} sistemati`);
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(`Errore durante la pulizia degli orfani`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid={`dialog-sistema-orfani-${mode}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            Sistema {labelEntitaPlur} orfani
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : orfani.length === 0 ? (
          <div className="py-12 text-center space-y-3">
            <div className="text-5xl">✨</div>
            <p className="text-muted-foreground">Nessun {labelEntita} orfano trovato.</p>
            <p className="text-xs text-muted-foreground">Tutte le {labelCategoria === "tipologia" ? "tipologie" : "categorie"} sono valide.</p>
          </div>
        ) : (
          <>
            <div className="space-y-1 text-sm text-muted-foreground">
              <p>Trovati <strong className="text-foreground">{orfani.length}</strong> {labelEntitaPlur} con {labelCategoria} mancante/eliminata.</p>
              <p>
                Suggerimenti automatici disponibili per <strong className="text-foreground">{conSuggerimento}</strong>.
                Modifica liberamente quelli che non ti convincono.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 py-2 border-y bg-muted/30 -mx-6 px-6">
              <Button
                size="sm"
                variant="outline"
                onClick={handleAcceptAll}
                disabled={conSuggerimento === 0}
                data-testid="btn-accetta-tutti-suggerimenti"
              >
                <ListChecks className="h-4 w-4 mr-1" /> Accetta tutti i suggerimenti ({conSuggerimento})
              </Button>
              <Button size="sm" variant="ghost" onClick={handleClearAll} data-testid="btn-clear-suggerimenti">
                Lascia tutti senza {labelCategoria}
              </Button>
            </div>

            <div className="overflow-y-auto flex-1 -mx-6 px-6 py-2 space-y-1">
              {orfani.map(o => {
                const nome = isFornitori ? o.ragione_sociale : o.nome;
                const sel = scelte[o.id] ?? "";
                return (
                  <div
                    key={o.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg hover:bg-muted/40"
                    data-testid={`riga-orfano-${o.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{nome}</p>
                      {o.suggested_nome && sel !== o.suggested_id && (
                        <p className="text-xs text-indigo-700">
                          <span aria-hidden>💡</span> suggerito: <strong>{o.suggested_nome}</strong>
                        </p>
                      )}
                    </div>
                    <Select
                      value={sel || "__none__"}
                      onValueChange={(v) => setScelte(prev => ({ ...prev, [o.id]: v === "__none__" ? "" : v }))}
                    >
                      <SelectTrigger className="w-full sm:w-56" data-testid={`select-categoria-${o.id}`}>
                        <SelectValue placeholder={`Senza ${labelCategoria}`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Senza {labelCategoria} —</SelectItem>
                        {categorieDisponibili.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.icona ? `${c.icona} ` : ""}{c.nome}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="btn-annulla-orfani">Annulla</Button>
          <Button
            onClick={handleApply}
            disabled={saving || loading || orfani.length === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="btn-applica-orfani"
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Applica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
